import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  advanceInsuranceClaim,
  createInsuranceClaim,
  INSURANCE_STATUS_FLOW,
  INSURANCE_STATUS_LABELS,
  listInsuranceClaims,
  type InsuranceClaim,
} from '@/lib/api/insurance.js';
import { formatCurrency, formatDateTime, prettyLabel } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { cn } from '@/lib/utils.js';
import { ChevronRight, Plus, Shield } from 'lucide-react';
import { useState } from 'react';

export function TicketInsuranceSection({ ticketId }: { ticketId: string }) {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [provider, setProvider] = useState('Safeware');
  const [claimNumber, setClaimNumber] = useState('');
  const [deductible, setDeductible] = useState('0');

  const claimsQuery = useQuery({
    queryKey: ['insurance-claims', ticketId],
    queryFn: () => listInsuranceClaims({ ticketId, pageSize: 10 }),
  });

  const createMut = useMutation({
    mutationFn: () =>
      createInsuranceClaim({
        ticketId,
        insuranceProvider: provider.trim() || 'Safeware',
        claimNumber: claimNumber.trim() || undefined,
        deductibleAmount: Number(deductible) || 0,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insurance-claims', ticketId] });
      setCreateOpen(false);
      setClaimNumber('');
      toastSuccess('Insurance claim created');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const advanceMut = useMutation({
    mutationFn: (claimId: string) => advanceInsuranceClaim(claimId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insurance-claims', ticketId] });
      toastSuccess('Claim advanced to next stage');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const claims = claimsQuery.data?.data ?? [];

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-500">
          <Shield className="h-4 w-4" />
          Insurance
        </h2>
        {!createOpen && claims.length === 0 ? (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-1 rounded-lg bg-primary px-2 py-1 text-xs font-bold text-white"
          >
            <Plus className="h-3 w-3" />
            Create claim
          </button>
        ) : null}
      </div>

      {createOpen ? (
        <form
          className="mb-4 space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            createMut.mutate();
          }}
        >
          <label className="block text-xs font-medium text-slate-600">
            Provider
            <input
              className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
            />
          </label>
          <label className="block text-xs font-medium text-slate-600">
            Claim #
            <input
              className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
              value={claimNumber}
              onChange={(e) => setClaimNumber(e.target.value)}
              placeholder="Optional"
            />
          </label>
          <label className="block text-xs font-medium text-slate-600">
            Deductible
            <input
              type="number"
              min={0}
              step="0.01"
              className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
              value={deductible}
              onChange={(e) => setDeductible(e.target.value)}
            />
          </label>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={createMut.isPending}
              className="rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50"
            >
              {createMut.isPending ? 'Creating…' : 'Create'}
            </button>
            <button
              type="button"
              onClick={() => setCreateOpen(false)}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {claimsQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading claims…</p>
      ) : claims.length === 0 && !createOpen ? (
        <p className="text-sm text-slate-500">No insurance claims for this ticket.</p>
      ) : (
        <div className="space-y-4">
          {claims.map((claim) => (
            <ClaimCard
              key={claim.id}
              claim={claim}
              onAdvance={() => advanceMut.mutate(claim.id)}
              advancing={advanceMut.isPending}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function ClaimCard({
  claim,
  onAdvance,
  advancing,
}: {
  claim: InsuranceClaim;
  onAdvance: () => void;
  advancing: boolean;
}) {
  const canAdvance =
    INSURANCE_STATUS_FLOW.includes(claim.status as (typeof INSURANCE_STATUS_FLOW)[number]) &&
    claim.status !== 'payment_received' &&
    claim.status !== 'denied' &&
    claim.status !== 'cancelled';

  const currentIdx = INSURANCE_STATUS_FLOW.indexOf(
    claim.status as (typeof INSURANCE_STATUS_FLOW)[number],
  );

  return (
    <div className="rounded-xl border border-slate-200 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-slate-950">{claim.insuranceProvider}</p>
          <p className="text-xs text-slate-500">
            {claim.claimNumber ? `#${claim.claimNumber}` : 'No claim number'} ·{' '}
            {prettyLabel(claim.status)}
          </p>
        </div>
        {claim.shopPayout ? (
          <span className="text-sm font-bold text-green-700">{formatCurrency(claim.shopPayout)}</span>
        ) : null}
      </div>

      <ol className="mt-3 space-y-1">
        {INSURANCE_STATUS_FLOW.map((status, idx) => {
          const done = currentIdx >= idx && currentIdx >= 0;
          const active = claim.status === status;
          return (
            <li
              key={status}
              className={cn(
                'flex items-center gap-2 text-xs',
                done ? 'text-slate-800' : 'text-slate-400',
                active && 'font-bold text-primary',
              )}
            >
              <span
                className={cn(
                  'h-2 w-2 rounded-full',
                  done ? 'bg-primary' : 'bg-slate-200',
                )}
              />
              {INSURANCE_STATUS_LABELS[status]}
            </li>
          );
        })}
      </ol>

      {claim.deductibleAmount && Number(claim.deductibleAmount) > 0 ? (
        <p className="mt-2 text-xs text-slate-600">
          Deductible: {formatCurrency(claim.deductibleAmount)}
        </p>
      ) : null}
      <p className="mt-1 text-[10px] text-slate-400">Updated {formatDateTime(claim.updatedAt)}</p>

      {canAdvance ? (
        <button
          type="button"
          disabled={advancing}
          onClick={onAdvance}
          className="mt-3 inline-flex items-center gap-1 rounded-lg bg-accent px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50"
        >
          Advance status
          <ChevronRight className="h-3 w-3" />
        </button>
      ) : null}
    </div>
  );
}
